import type { DatumEngine } from '@datum-sdk/engine';
import type { DatumEditor, DebugOverlay, HistoryManager } from '@datum-sdk/editor';
import { PRELOADER_ROUTE_IDS, PRELOADER_SKIP_ROUTE_ID } from './nativeRouteCinematic';
import { departurePositions, repaceSmoothRuns } from './routes/repaceSmoothRuns';

/**
 * Splat-scene tooling from `@datum-sdk/editor`: the SDK's own camera-route
 * editor, a player that flies a route the way the preloader does and a copy-out
 * of the edited routes (`?flight`), and the orbit-pivot marker (`?dev`).
 *
 * Owned by `createSdkScene`, not by the React panel: the editor draws in a
 * second WebGL context that must be released before `engine.destroy()`, and
 * only the scene's own teardown runs in that order. The package is imported on
 * first use, so production never downloads it.
 */
export interface DevEditorTools {
  /** Routes an author edits and plays — the host-built skip glide is not one. */
  routeIds: () => string[];
  /** Resolves `false` when refused: the camera is busy (`DevEditorToolsHost.canTakeCamera`). */
  setEditing: (on: boolean) => Promise<boolean>;
  selectRoute: (id: string) => void;
  /** Ignored for a looping route, while the editor loads, or while the camera is busy. */
  playRoute: (id: string) => void;
  stopRoute: () => void;
  copyRoutes: () => void;
  setOrbitCenterShown: (on: boolean) => void;
  dispose: () => void;
}

export interface DevEditorToolsHost {
  /** False while the preloader, a scene transition or a POI flight owns the camera. */
  canTakeCamera: () => boolean;
  /** Routes and the editor's framing flights wipe the scene's pitch constraint. */
  armCameraConstraints: () => void;
  /** The scene runs collisions — the player lifts them for the flight. */
  collisions: boolean;
}

const loadEditorPackage = () => import('@datum-sdk/editor');

/** The SDK's undo hotkeys check no target — keep Cmd+Z in a text field the field's. */
const EDITABLE_TARGETS = 'input, textarea, select, [contenteditable]';

export const createDevEditorTools = (
  engine: DatumEngine,
  host: DevEditorToolsHost,
): DevEditorTools => {
  let disposed = false;

  const authoredRoutes = () =>
    engine.getCameraRoutes().filter((route) => route.id !== PRELOADER_SKIP_ROUTE_ID);
  const routeIds = (): string[] => authoredRoutes().map((route) => route.id);

  let editor: DatumEditor | null = null;
  let history: HistoryManager | null = null;
  // Asked for: `editor` stays null until the lazy import lands.
  let editing = false;

  // ── Player: the preloader's harness around one route ─────────────────────
  // Mirrors the preloader: controls off for the flight, collisions lifted, and
  // the constraints a route wiped re-armed once it lands — so a preview lands,
  // clamps and snaps as the real one would.
  let playing: string | null = null;
  let liftedCollisions = false;

  const release = (): void => {
    if (disposed) return;
    if (liftedCollisions) {
      liftedCollisions = false;
      engine.setRuntimeOptions({ collisions: { enabled: true } });
    }
    engine.setRuntimeOptions({ controls: { enabled: true } });
    host.armCameraConstraints();
  };

  const onRouteDone = (event: { routeId: string }): void => {
    if (event.routeId !== playing) return;
    playing = null;
    // The engine hands the camera back right after this emit — restore after it.
    queueMicrotask(release);
  };
  engine.on('route:end', onRouteDone);
  engine.on('route:stop', onRouteDone);

  const playRoute = (id: string): void => {
    // Not while the editor is loading: it would snapshot the lifted controls
    // and collisions as the scene's own and put them back on close.
    if (disposed || !host.canTakeCamera() || (editing && !editor)) return;
    const route = authoredRoutes().find((candidate) => candidate.id === id);
    if (!route) return;
    if (route.loop) {
      // It never lands, so the harness would hold the loop at full rate until Stop.
      console.warn(`[DevEditorTools] "${id}" loops — watch it with ?dev → Preloader Hold instead.`);
      return;
    }
    if (playing) {
      // Restart: the harness is already up, so do not hand the camera back between.
      playing = null;
      engine.stopCameraRoute();
    } else {
      engine.setRuntimeOptions({ controls: { enabled: false } });
      // An open editor already holds the collisions off, and puts them back itself.
      liftedCollisions = host.collisions && !editor;
      if (liftedCollisions) engine.setRuntimeOptions({ collisions: { enabled: false } });
    }
    playing = id;
    engine.startCameraRoute(id);
    // A route without waypoints is declined silently and emits nothing.
    if (playing === id && engine.getActiveCameraRouteId() !== id) {
      playing = null;
      release();
    }
  };

  const stopRoute = (): void => {
    if (playing) engine.stopCameraRoute();
  };

  // An edit leaves a view's bezier asking for speeds its new neighbours no
  // longer match; the copy mends them (`repaceSmoothRuns`), so what is pasted
  // into the scene flies at one continuous speed.
  const copyRoutes = (): void => {
    const routes = authoredRoutes();
    // The flyover leaves from where the exit parks the camera — its run's entry.
    const exit = routes.find((route) => route.id === PRELOADER_ROUTE_IDS.exit);
    const parked = exit && departurePositions(exit.waypoints).landing;
    const repaced = routes.map((route) => ({
      ...route,
      waypoints: repaceSmoothRuns(
        route,
        route.id === PRELOADER_ROUTE_IDS.flyover ? parked : undefined,
      ),
    }));
    const json = JSON.stringify(repaced, null, 2);
    // No clipboard off a secure origin (a phone on `--host`) or an unfocused page.
    const copied =
      navigator.clipboard?.writeText(json) ?? Promise.reject(new Error('clipboard unavailable'));
    copied.catch((error: unknown) => {
      console.error('[DevEditorTools] Copy failed, the routes follow:', error, `\n${json}`);
    });
  };

  // ── Editor ───────────────────────────────────────────────────────────────
  // One editor + undo history per editing session, disposed on close: the
  // history listens on `window` from its constructor, and left alive it would
  // let a stray Cmd+Z rewrite a route with the editor closed. Whatever the
  // session wrote stays in the engine (`putCameraRoute`) for Copy Routes.
  //
  // The editor takes over collisions, POI interaction, declutter/clustering
  // and the movement speed while open and hands them back on dispose. A route
  // still playing at either end is stopped first: flipping collisions under a
  // flight in an orbit scene switches the camera mode.
  let selectedRoute: string | null = null;

  const closeEditor = (): void => {
    if (!editor) return;
    editor.dispose();
    history?.dispose();
    editor = null;
    history = null;
    // Its framing flights wiped the scene's pitch constraint.
    host.armCameraConstraints();
  };

  const setEditing = async (on: boolean): Promise<boolean> => {
    if (!on) {
      editing = false;
      stopRoute();
      closeEditor();
      return true;
    }
    if (disposed || !host.canTakeCamera()) return false;
    editing = true;
    // Released before the await resolves, so the editor snapshots a scene with
    // its controls and collisions back.
    stopRoute();
    const { DatumEditor: Editor, HistoryManager: History } = await loadEditorPackage();
    if (disposed || !editing) return false;
    if (editor) return true;
    history = new History({
      hotkeyTarget: 'window',
      maxSize: 100,
      ignoreSelector: EDITABLE_TARGETS,
    });
    editor = new Editor(engine, history);
    editor.setType('camera-route', true);
    // Nothing is drawn until a route is selected; selecting frames it.
    if (selectedRoute) editor.route.selectRoute(selectedRoute, true);
    return true;
  };

  const selectRoute = (id: string): void => {
    selectedRoute = id;
    if (editing && editor) editor.route.selectRoute(id, true);
  };

  // ── Orbit-pivot marker ───────────────────────────────────────────────────
  let overlay: DebugOverlay | null = null;
  let overlayWanted = false;

  const setOrbitCenterShown = (on: boolean): void => {
    overlayWanted = on;
    if (!on) {
      overlay?.dispose();
      overlay = null;
      return;
    }
    if (overlay) return;
    void loadEditorPackage().then(({ DebugOverlay: Overlay }) => {
      if (disposed || !overlayWanted || overlay) return;
      overlay = new Overlay(engine, { showGrid: false, showAxes: false, showLookAtPoint: true });
    });
  };

  return {
    routeIds,
    setEditing,
    selectRoute,
    playRoute,
    stopRoute,
    copyRoutes,
    setOrbitCenterShown,
    dispose: () => {
      if (disposed) return;
      disposed = true;
      engine.off('route:end', onRouteDone);
      engine.off('route:stop', onRouteDone);
      overlay?.dispose();
      overlay = null;
      closeEditor();
    },
  };
};
